import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Save, ArrowLeft, Send, Trash2, ExternalLink, Sparkles, Loader2, Linkedin } from "lucide-react";
import { adminApi } from "@/lib/adminApi";
import { ImageField } from "@/components/admin/Uploads";
import { RepurposePanel } from "@/components/admin/RepurposePanel";

const STATUSES = ["idea", "researching", "ai_draft", "review", "approved", "scheduled", "published"];
const CATEGORIES = ["Artificial Intelligence", "Education", "Future of Work", "Skills", "Economy", "Policy", "Workforce", "Leadership", "Responsible AI", "Adaptive Intelligence"];
const EMPTY = {
  title: "", subtitle: "", slug: "", category: "", tags: [], author: "Raulvin Coke",
  featured_image: "", excerpt: "", body: "", what_happened: "", why_it_matters: "",
  who_should_care: [], raulvins_signal: "", what_to_watch: [], sources: [],
  seo_title: "", seo_description: "", social_description: "", status: "idea", scheduled_date: "",
};
const box = "bg-[var(--bg-card)] border-glow rounded-xl p-6";
const label = "text-xs text-[var(--text-secondary)] uppercase tracking-wider mb-2 block";
const field = "w-full bg-[var(--bg-secondary)] border border-[rgba(124,58,237,0.15)] rounded-lg p-3 text-sm text-white placeholder:text-[var(--text-secondary)]/40 resize-y focus:outline-none focus:border-[var(--accent-violet)]";

const toLines = (arr) => (arr || []).join("\n");
const fromLines = (t) => t.split("\n").map((s) => s.trim()).filter(Boolean);
const sourcesToText = (arr) => (arr || []).map((s) => (s.url ? `${s.title} | ${s.url}` : s.title)).join("\n");
const textToSources = (t) => fromLines(t).map((l) => { const [title, url = ""] = l.split("|").map((x) => x.trim()); return { title, url }; });

const Area = ({ title, value, onChange, rows = 4, testId, placeholder }) => (
  <div className={box}>
    <label className={label}>{title}</label>
    <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={rows} placeholder={placeholder} className={field} data-testid={testId} />
  </div>
);

const ArticleEditor = () => {
  const { id } = useParams();
  const isNew = !id;
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [lists, setLists] = useState({ who: "", watch: "", sources: "", tags: "" });
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!id) return;
    adminApi(`/admin/articles/${id}`)
      .then((a) => {
        const merged = { ...EMPTY, ...Object.fromEntries(Object.entries(a).filter(([, v]) => v !== null)) };
        setForm(merged);
        setLists({ who: toLines(merged.who_should_care), watch: toLines(merged.what_to_watch), sources: sourcesToText(merged.sources), tags: (merged.tags || []).join(", ") });
        setLoaded(true);
      })
      .catch((e) => { toast.error(e.message); navigate("/admin/articles"); });
  }, [id, navigate]);

  const up = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const upList = (k, v) => setLists((p) => ({ ...p, [k]: v }));
  const [manualCover, setManualCover] = useState(false);
  const coverBusy = manualCover || form.cover_status === "generating";

  useEffect(() => {
    if (!id || form.cover_status !== "generating" || manualCover) return;
    const t = setInterval(() => {
      adminApi(`/admin/articles/${id}`).then((a) => {
        if (a.cover_status !== "generating") setForm((p) => ({ ...p, featured_image: a.featured_image || p.featured_image, cover_status: a.cover_status }));
      }).catch(() => {});
    }, 5000);
    return () => clearInterval(t);
  }, [id, form.cover_status, manualCover]);

  const [seoBusy, setSeoBusy] = useState(false);
  const fillSeo = async () => {
    setSeoBusy(true);
    try {
      const { _id, created_at, updated_at, created_by, published_date, repurposed, cover_status, image_prompt, ...rest } = form;
      const seo = await adminApi("/admin/seo", { method: "POST", body: { ...rest, who_should_care: fromLines(lists.who), what_to_watch: fromLines(lists.watch), sources: textToSources(lists.sources), tags: [] } });
      setForm((p) => ({ ...p, ...seo }));
      toast.success("SEO & social text filled — remember to Save");
    } catch (e) { toast.error(e.message); }
    finally { setSeoBusy(false); }
  };

  const genCover = async () => {
    setManualCover(true);
    try {
      const { url } = await adminApi(`/admin/articles/${id}/cover`, { method: "POST" });
      setForm((p) => ({ ...p, featured_image: url, cover_status: "done" }));
      toast.success("AI cover generated");
    } catch (e) { toast.error(e.message); setForm((p) => ({ ...p, cover_status: "failed" })); }
    finally { setManualCover(false); }
  };

  const save = async (publish = false) => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    const { _id, created_at, updated_at, created_by, published_date, repurposed, cover_status, image_prompt, ...rest } = form;
    const payload = {
      ...rest,
      who_should_care: fromLines(lists.who), what_to_watch: fromLines(lists.watch),
      sources: textToSources(lists.sources), tags: lists.tags.split(",").map((t) => t.trim()).filter(Boolean),
      status: publish ? "published" : form.status,
    };
    try {
      const data = await adminApi(isNew ? "/admin/articles" : `/admin/articles/${id}`, { method: isNew ? "POST" : "PUT", body: payload });
      toast.success(publish ? "Article published" : "Article saved");
      setForm((p) => ({ ...p, slug: data.slug, status: payload.status }));
      if (isNew) navigate(`/admin/articles/${data.id}/edit`, { replace: true });
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const del = async () => {
    if (!window.confirm("Delete this article?")) return;
    await adminApi(`/admin/articles/${id}`, { method: "DELETE" }).catch((e) => toast.error(e.message));
    toast.success("Article deleted");
    navigate("/admin/articles");
  };

  return (
    <div className="max-w-5xl" data-testid="article-editor">
      <div className="flex items-center justify-between mb-6">
        <button onClick={() => navigate("/admin/articles")} className="flex items-center gap-2 text-sm text-[var(--text-secondary)] hover:text-white transition-colors" data-testid="editor-back-btn">
          <ArrowLeft className="w-4 h-4" /> Articles
        </button>
        <div className="flex gap-2">
          {!isNew && form.status === "published" && form.slug && (
            <>
              <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(`${window.location.origin}/intelligence/${form.slug}`)}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-[#70b5f9] hover:bg-[#0a66c2]/15 px-3 py-2 rounded-lg text-sm transition-colors" data-testid="share-linkedin-btn">
                <Linkedin className="w-4 h-4" /> Share to LinkedIn
              </a>
              <a href={`/intelligence/${form.slug}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-[var(--accent-violet-light)] hover:bg-[var(--accent-violet)]/10 px-3 py-2 rounded-lg text-sm transition-colors" data-testid="view-live-btn">
                <ExternalLink className="w-4 h-4" /> View live
              </a>
            </>
          )}
          {!isNew && (
            <button onClick={del} className="flex items-center gap-1.5 text-red-400 hover:bg-red-400/10 px-3 py-2 rounded-lg text-sm transition-colors" data-testid="delete-article-btn">
              <Trash2 className="w-4 h-4" /> Delete
            </button>
          )}
          <button onClick={() => save(false)} disabled={saving} className="flex items-center gap-1.5 bg-[var(--bg-card)] border-glow text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[var(--accent-violet)]/10 transition-colors disabled:opacity-60" data-testid="save-btn">
            <Save className="w-4 h-4" /> Save
          </button>
          <button onClick={() => save(true)} disabled={saving} className="flex items-center gap-1.5 bg-[var(--accent-violet)] hover:bg-[#6d28d9] text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors disabled:opacity-60" data-testid="publish-btn">
            <Send className="w-4 h-4" /> Publish
          </button>
        </div>
      </div>

      <div className="space-y-6">
        <div className={`${box} space-y-4`}>
          <input value={form.title} onChange={(e) => up("title", e.target.value)} placeholder="Article Title" className="w-full text-2xl font-bold bg-transparent text-white placeholder:text-[var(--text-secondary)]/40 focus:outline-none" data-testid="article-title" />
          <input value={form.subtitle} onChange={(e) => up("subtitle", e.target.value)} placeholder="Subtitle / dek" className="w-full text-base bg-transparent text-[var(--text-secondary)] placeholder:text-[var(--text-secondary)]/40 focus:outline-none" data-testid="article-subtitle" />
          <div className="grid md:grid-cols-4 gap-3">
            <select value={form.category} onChange={(e) => up("category", e.target.value)} className={field} data-testid="article-category">
              <option value="">Category</option>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select value={form.status} onChange={(e) => up("status", e.target.value)} className={field} data-testid="article-status">
              {STATUSES.map((s) => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1).replace("_", " ")}</option>)}
            </select>
            <input value={form.slug} onChange={(e) => up("slug", e.target.value)} placeholder="slug (auto)" className={field} data-testid="article-slug" />
            <input type="date" value={(form.scheduled_date || "").slice(0, 10)} onChange={(e) => up("scheduled_date", e.target.value)} className={field} data-testid="article-scheduled-date" />
          </div>
          <div className="grid md:grid-cols-2 gap-3 items-center">
            <div className="flex flex-wrap items-center gap-3">
              <ImageField value={form.featured_image} onChange={(v) => up("featured_image", v)} testId="article-featured-image" />
              {!isNew && (
                <button type="button" onClick={genCover} disabled={coverBusy} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm bg-[var(--accent-violet)]/15 text-[var(--accent-violet-light)] hover:bg-[var(--accent-violet)]/25 transition-colors disabled:opacity-60" data-testid="generate-cover-btn">
                  {coverBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} {coverBusy ? "Generating cover..." : "Generate AI cover"}
                </button>
              )}
              {form.cover_status === "failed" && !coverBusy && <span className="text-xs text-red-400" data-testid="cover-failed">Cover generation failed — try again</span>}
            </div>
            <input value={lists.tags} onChange={(e) => upList("tags", e.target.value)} placeholder="Tags, comma separated" className={field} data-testid="article-tags" />
          </div>
          <textarea value={form.excerpt} onChange={(e) => up("excerpt", e.target.value)} rows={2} placeholder="Excerpt shown on article cards" className={field} data-testid="article-excerpt" />
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          <div className="space-y-4">
            <Area title="What Happened" value={form.what_happened} onChange={(v) => up("what_happened", v)} rows={5} testId="article-what-happened" />
            <Area title="Why It Matters" value={form.why_it_matters} onChange={(v) => up("why_it_matters", v)} rows={5} testId="article-why-it-matters" />
            <Area title="Who Should Care (one per line)" value={lists.who} onChange={(v) => upList("who", v)} testId="article-who-should-care" />
          </div>
          <div className="space-y-4">
            <div className="bg-[var(--bg-card)] border border-[var(--accent-violet)]/20 rounded-xl p-6">
              <label className="text-xs text-[var(--accent-violet-light)] uppercase tracking-wider mb-2 block font-semibold">Raulvin's Signal</label>
              <textarea value={form.raulvins_signal} onChange={(e) => up("raulvins_signal", e.target.value)} rows={6} placeholder="Your unique perspective on this development..." className={field} data-testid="raulvins-signal-input" />
            </div>
            <Area title="What to Watch Next (one per line)" value={lists.watch} onChange={(v) => upList("watch", v)} testId="article-what-to-watch" />
            <Area title="Sources (Title | URL, one per line)" value={lists.sources} onChange={(v) => upList("sources", v)} testId="article-sources" placeholder="World Economic Forum | https://weforum.org/..." />
          </div>
        </div>

        <Area title="Full Analysis / Body (optional, blank line between paragraphs)" value={form.body} onChange={(v) => up("body", v)} rows={8} testId="article-body" />

        <div className={`${box} grid md:grid-cols-2 gap-3`}>
          <div className="md:col-span-2 flex items-center justify-between">
            <label className={label + " !mb-0"}>SEO & Social</label>
            <button type="button" onClick={fillSeo} disabled={seoBusy} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs bg-[var(--accent-violet)]/15 text-[var(--accent-violet-light)] hover:bg-[var(--accent-violet)]/25 transition-colors disabled:opacity-60" data-testid="fill-seo-btn">
              {seoBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} {seoBusy ? "Writing..." : "Fill SEO with AI"}
            </button>
          </div>
          <input value={form.seo_title} onChange={(e) => up("seo_title", e.target.value)} placeholder="SEO Title" className={field} data-testid="article-seo-title" />
          <input value={form.social_description} onChange={(e) => up("social_description", e.target.value)} placeholder="Social description" className={field} data-testid="article-social-description" />
          <textarea value={form.seo_description} onChange={(e) => up("seo_description", e.target.value)} placeholder="SEO Description" rows={2} className={`${field} md:col-span-2`} data-testid="article-seo-description" />
        </div>

        {!isNew && loaded && <RepurposePanel articleId={id} initial={form.repurposed || {}} />}
      </div>
    </div>
  );
};

export default ArticleEditor;
